import { isFirefox } from '@iconik-io/core/src/browser/is_firefox';

export const HIDDEN_MEDIA_RELEASE_DELAY_MS = 10_000;

type HiddenMediaReleaseOptions = {
  isPlaying?: () => boolean;
};

type ReleasedSource = {
  src: string;
  currentTime: number;
  playbackRate: number;
};

/**
 * Firefox keeps a paused media element's download connection open, and a
 * storage host allows six per browser, so a few background tabs can stall
 * every other request to that host.
 */
export class HiddenMediaRelease {
  #element: HTMLMediaElement;

  #enabled: boolean;

  #isPlaying: () => boolean;

  #timer: ReturnType<typeof setTimeout> | undefined;

  #released: ReleasedSource | undefined;

  constructor(
    element: HTMLMediaElement,
    { isPlaying = () => false }: HiddenMediaReleaseOptions = {},
  ) {
    this.#element = element;
    this.#enabled = isFirefox();
    this.#isPlaying = isPlaying;
  }

  get isReleased(): boolean {
    return this.#released !== undefined;
  }

  start(): void {
    if (!this.#enabled) {
      return;
    }
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#element.addEventListener('pause', this.schedule);
    this.#element.addEventListener('loadeddata', this.schedule);
    this.#element.addEventListener('play', this.#cancel);
    this.schedule();
  }

  restore = (): void => {
    const released = this.#released;
    if (!released) {
      return;
    }
    this.#cancel();
    this.#released = undefined;
    this.#element.src = released.src;
    this.#element.load();
    this.#element.currentTime = released.currentTime;
    this.#element.playbackRate = released.playbackRate;
  };

  destroy(): void {
    this.#cancel();
    document.removeEventListener(
      'visibilitychange',
      this.#handleVisibilityChange,
    );
    this.#element.removeEventListener('pause', this.schedule);
    this.#element.removeEventListener('loadeddata', this.schedule);
    this.#element.removeEventListener('play', this.#cancel);
  }

  #handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.#cancel();
      this.restore();
      return;
    }
    this.schedule();
  };

  schedule = (): void => {
    if (this.#timer !== undefined || this.#released || !this.#canRelease()) {
      return;
    }
    this.#timer = setTimeout(this.#release, HIDDEN_MEDIA_RELEASE_DELAY_MS);
  };

  #cancel = () => {
    clearTimeout(this.#timer);
    this.#timer = undefined;
  };

  #canRelease() {
    return (
      document.visibilityState === 'hidden' &&
      this.#element.paused &&
      !this.#isPlaying() &&
      this.#element.hasAttribute('src')
    );
  }

  #release = () => {
    this.#timer = undefined;
    if (!this.#canRelease()) {
      return;
    }
    this.#released = {
      src: this.#element.src,
      currentTime: this.#element.currentTime,
      playbackRate: this.#element.playbackRate,
    };
    this.#element.removeAttribute('src');
    this.#element.load();
  };
}
